import { Box } from '@chakra-ui/react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

// The pass previews are authored at fixed design widths; the scalers below shrink
// them to fit a responsive container while preserving aspect ratio.
export const PREVIEW_DESIGN_WIDTH = 343;
export const GOOGLE_PREVIEW_DESIGN_WIDTH = 295;
export const APPLE_PREVIEW_DESIGN_WIDTH = 289;

/**
 * Scales a pass preview to fill its container's width (preserving aspect ratio)
 * and collapses the wrapper to the scaled height. Used by the templates list
 * and the prebuilt-template picker so previews render at a consistent size.
 */
export const ScaledPassPreview = ({ children }: { children: ReactNode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const measure = () => {
      const containerWidth = containerRef.current?.offsetWidth;
      const contentHeight = contentRef.current?.offsetHeight;
      if (!containerWidth || !contentHeight) return;
      const nextScale = containerWidth / PREVIEW_DESIGN_WIDTH;
      setScale(nextScale);
      setHeight(contentHeight * nextScale);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      position="relative"
      w="100%"
      overflow="hidden"
      height={height ? `${height}px` : undefined}
    >
      <Box
        ref={contentRef}
        width={`${PREVIEW_DESIGN_WIDTH}px`}
        transformOrigin="top left"
        style={{ transform: `scale(${scale})` }}
      >
        {children}
      </Box>
    </Box>
  );
};

/**
 * Scales a pass preview to a fixed target height (preserving aspect ratio).
 * `designWidth` lets callers scale Apple/Google previews, which are authored
 * at narrower widths than the shared default.
 */
export const ScaledPassPreviewByHeight = ({
  children,
  targetHeight,
  designWidth = PREVIEW_DESIGN_WIDTH,
}: {
  children: ReactNode;
  targetHeight: number;
  designWidth?: number;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const measure = () => {
      const naturalHeight = contentRef.current?.offsetHeight;
      if (!naturalHeight) return;
      const nextScale = targetHeight / naturalHeight;
      setScale(nextScale);
      setWidth(designWidth * nextScale);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [targetHeight, designWidth]);

  return (
    <Box
      position="relative"
      overflow="hidden"
      height={`${targetHeight}px`}
      width={width ? `${width}px` : undefined}
      flexShrink={0}
    >
      <Box
        ref={contentRef}
        width={`${designWidth}px`}
        transformOrigin="top left"
        style={{ transform: `scale(${scale})` }}
      >
        {children}
      </Box>
    </Box>
  );
};
